import { useState, useEffect } from "react";
// Frontend BFF path: nginx rewrites /chat-bff/* to the Next.js proxy, which
// injects the chat service token server-side. Same-origin, no token in the browser.
const STREAM_API_URL = "/chat-bff/drought_chat_stream/";
const ASYNC_API_URL = "/chat-bff/drought_chat_async/";
const ASYNC_STATUS_BASE_URL = "/chat-bff/drought_chat_status/";
const POLL_INTERVAL_MS = 2000;
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

function getCacheKey(query) {
  return "aiOverview_" + query;
}

function readCache(query) {
  try {
    const raw = sessionStorage.getItem(getCacheKey(query));
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (Date.now() - cached.ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(getCacheKey(query));
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function writeCache(query, response, chartData) {
  try {
    sessionStorage.setItem(
      getCacheKey(query),
      JSON.stringify({ response, chartData, ts: Date.now() })
    );
  } catch {
    // storage full — ignore
  }
}

async function* parseSSE(reader) {
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";
    for (const part of parts) {
      if (!part.trim()) continue;
      let event = "message";
      let data = "";
      for (const line of part.split("\n")) {
        if (line.startsWith("event: ")) event = line.slice(7).trim();
        else if (line.startsWith("data: ")) data = line.slice(6);
      }
      if (data) {
        try { yield { event, data: JSON.parse(data) }; } catch { /* skip */ }
      }
    }
  }
}

/**
 * Auto-fires a single chat query on mount.
 * Tries SSE streaming first; falls back to async polling if unavailable.
 * Caches the result in sessionStorage for CACHE_TTL_MS.
 */
export default function useAutoChat(query) {
  const cached = readCache(query);
  const [response, setResponse] = useState(cached?.response ?? null);
  const [chartData, setChartData] = useState(cached?.chartData ?? {});
  const [mapImages, setMapImages] = useState(cached?.mapImages ?? []);
  const [isLoading, setIsLoading] = useState(!cached);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (readCache(query)) return;

    let cancelled = false;
    let pollTimer = null;
    const controller = new AbortController();

    // ---- Streaming path ----
    async function tryStream() {
      const res = await fetch(STREAM_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: query, skip_enrichment: true }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error("stream_unavailable");

      const reader = res.body.getReader();
      let accumulated = "";
      let charts = {};

      for await (const { event, data } of parseSSE(reader)) {
        if (cancelled) break;
        switch (event) {
          case "progress":
            setProgress(data);
            break;
          case "tool_data":
            charts = extractChartData(data.tool_outputs || []);
            setChartData(charts);
            break;
          case "token":
            accumulated += data.text || "";
            setResponse(accumulated);
            break;
          case "error":
            throw new Error(data.message || "Stream error");
          default:
            break;
        }
      }

      if (!cancelled) {
        const text = accumulated || "No response received.";
        setResponse(text);
        setIsLoading(false);
        setProgress(null);
        writeCache(query, text, charts);
      }
    }

    // ---- Polling fallback ----
    async function pollJob(jobId) {
      if (cancelled) return;
      try {
        const res = await fetch(`${ASYNC_STATUS_BASE_URL}${jobId}/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch job status.");
        const data = await res.json();
        if (cancelled) return;

        if (data.progress && data.progress.step) setProgress(data.progress);

        if (data.status === "completed") {
          const text = data.response || "No response received.";
          const charts = extractChartData(data.tool_outputs);
          setResponse(text);
          setChartData(charts);
          setIsLoading(false);
          setProgress(null);
          writeCache(query, text, charts);
          return;
        }
        if (data.status === "failed") {
          setError(data.error || "The background job failed.");
          setIsLoading(false);
          setProgress(null);
          return;
        }
        pollTimer = setTimeout(() => pollJob(jobId), POLL_INTERVAL_MS);
      } catch (err) {
        if (!cancelled && err.name !== "AbortError") {
          setError(err.message || "Something went wrong.");
          setIsLoading(false);
        }
      }
    }

    async function fallbackPoll() {
      const res = await fetch(ASYNC_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: query, skip_enrichment: true }),
        signal: controller.signal,
      });
      if (!res.ok) {
        let detail = "Failed to send message.";
        try {
          const d = await res.json();
          detail = d.detail || JSON.stringify(d);
        } catch { /* ignore */ }
        throw new Error(detail);
      }
      const data = await res.json();
      if (!cancelled) await pollJob(data.job_id);
    }

    // ---- Main: try stream, fall back to poll ----
    (async () => {
      try {
        await tryStream();
      } catch (streamErr) {
        if (cancelled || streamErr.name === "AbortError") return;
        try {
          await fallbackPoll();
        } catch (pollErr) {
          if (!cancelled && pollErr.name !== "AbortError") {
            setError(pollErr.message || "Something went wrong.");
            setIsLoading(false);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [query]);

  return { response, chartData, mapImages, isLoading, error, progress };
}

function extractChartData(toolOutputs) {
  if (!toolOutputs || toolOutputs.length === 0) return {};
  for (const output of toolOutputs) {
    const cd = output?.result?.chart_data;
    if (cd) return cd;
  }
  return {};
}

function extractMapImages(toolOutputs) {
  if (!toolOutputs || toolOutputs.length === 0) return [];
  for (const output of toolOutputs) {
    const imgs = output?.result?.map_images;
    if (imgs && imgs.length > 0) return imgs;
  }
  return [];
}
